import { useEffect, useState, useCallback, useRef } from 'react';
import * as d3 from 'd3';
import { useQuery, gql } from '@apollo/client';
import { defaultScope as scope } from '../utility/getScope';

const GetHistmapDataDocument = gql`
  query GetHistmapData(
    $id: ID!
    $identifierType: String!
    $scope: ChartScopeInput!
    $breakdowns: [ChartBreakdownInput!]
  ) {
    chart(id: $id, identifierType: $identifierType, scope: $scope, breakdowns: $breakdowns) {
      header {
        breakdowns {
          breakdown
          filter
        }
      }
      table {
        columns
        rows
      }
    }
  }
`;

export const useHistmap = (
  organization: string,
  project: string,
  round: string,
  variation: string,
  segment: string | null = null,
  dataLimit?: number,
) => {
  const [imageReady, setImageReady] = useState(false);
  const [invalidData, setInvalidData] = useState<string | null>(null);
  const [renderedSvg, setRenderedSvg] = useState<d3.Selection<d3.BaseType, unknown, HTMLElement, any> | null>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      setImageReady(false);
    }
  }, [round, variation, segment]);

  const resizeObserver = new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
    const height = entries[0].contentRect.height;
    if (height >= 200) {
      setImageReady(true);
      //!renderedSvg && render();
    }
  });

  useEffect(() => {
    containerRef.current && resizeObserver.observe(containerRef.current);
    return (): void => {
      //unmount
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (renderedSvg) {
      renderedSvg.selectAll('svg').remove();
      setRenderedSvg(null);
    }
  }, [organization, project, round, variation, segment]);

  const render = useCallback(
    (histmapData, invalid) => {
      if (imageReady && histmapData.length) {
        const element = d3.select(`#${containerRef.current!.id}`);
        const { width: innerWidth, height: innerHeight } = (
          element.select('img').node() as Element
        ).getBoundingClientRect();

        let density: number[] = [];
        const height = Math.round(innerHeight);
        const lins = Array.from(Array(height), (_, index) => index * (1 / height));
        const length = Math.round(height / 200);
        histmapData.reduce((p: number, c: number, i: number) => {
          if (p !== undefined && p !== null && i !== 0) {
            const expandedData = Array.from({ length: length }, (_, i) => p - Math.round((p - c) * (i / length)));
            density = density.concat(expandedData);
          }
          return c;
        }, null);

        const ticksIndices = [90, 80, 70, 60, 50, 40, 30, 20, 10]
          .map((tick) => density.reduce(nearest(tick)))
          .map((value) => density.indexOf(value));

        const data = {
          value: lins,
          density: density,
        };

        d3
          .scaleLinear() /* x = */
          .domain(d3.extent(data.value) as [number, number])
          .range([0, innerWidth]),
          d3
            .scaleLinear() /* y = */
            .domain([0, d3.max(data.density) as number])
            .range([0, innerHeight]);

        const color = d3.scaleSequentialSqrt([0, d3.max(data.density) as number], d3.interpolateTurbo);
        const svg = element
          .append('svg')
          .attr('width', '100%')
          .attr('height', '100%')
          .style('position', 'absolute')
          .style('top', '0')
          .style('left', '0');

        svg
          .append('rect')
          .attr('y', 0)
          .attr('x', 0)
          .attr('width', '100%')
          .attr('height', '100%')
          .attr('fill', 'rgba(0, 0, 0, 0.4)')
          .style('position', 'absolute')
          .style('top', '0')
          .style('left', '0');

        svg
          .selectAll('rect .bar')
          .data(data.value.map((_, i) => i))
          .enter()
          .append('rect')
          .classed('bar', true)
          .attr('y', (d) => d - 1)
          .attr('x', (_, i) =>
            !invalid && data.density[i] ? (innerWidth - Math.floor(innerWidth * (data.density[i] / 100))) / 2 : 0,
          )
          .attr('width', (_, i) =>
            !invalid && data.density[i] ? Math.floor(innerWidth * (data.density[i] / 100)) : '0%',
          )
          .attr('height', '1px')
          .attr('fill', () => (!invalid ? 'rgba(235, 235, 0, 0.6)' : color(0)))
          .style('position', 'absolute')
          .style('top', '0')
          .style('left', '0')
          .style('opacity', '0.7');

        if (!invalid) {
          const tickElement = svg.selectAll('g').data(ticksIndices).enter().append('g').classed('ticks', true);

          tickElement
            .append('rect')
            .attr('y', (d) => d - 1)
            .attr('width', '100%')
            .attr('height', '1px')
            .attr('fill', 'rgba(255, 255, 255, 1)')
            .style('position', 'absolute')
            .style('top', '0')
            .style('left', '0');

          tickElement
            .append('text')
            .attr('y', (d) => d - 1)
            .attr('x', innerWidth / 2 - 10)
            .attr('fill', 'rgba(255, 255, 255, 1)')
            .text((_, i) => `${9 - i}0%`)
            .style('position', 'absolute')
            .style('top', '0')
            .style('left', '0');
        }

        setRenderedSvg(element);
        !invalid && setInvalidData(null);
      }
    },
    [imageReady, renderedSvg],
  );

  const { error, loading } = useQuery(GetHistmapDataDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      id: `planbcd_histmap_${organization}`,
      identifierType: 'resource_key',
      ...scope,
      breakdowns: [
        { breakdown: 'project_id', filter: [project] },
        { breakdown: 'round_id', filter: [round] },
        { breakdown: 'variation_id', filter: [variation] },
        { breakdown: 'segment', filter: segment ? [segment] : null },
      ],
      options: {
        sampling: {
          type: 'SIZE',
          number: 40000, // sample size used in Sitedock v1
        },
      },
      ...(dataLimit
        ? {
            options: {
              sampling: {
                type: 'RATE',
                number: dataLimit,
              },
            },
          }
        : {}),
    },
    context: {
      clientName: 'dataplatform',
    },
    onCompleted: (data) => {
      const lins = Array(200).fill(0);
      const dataIndex = data.chart.table.columns.indexOf('json_data');

      const totalLogs = data.chart.table.rows.reduce((prev: any, current: any) => {
        return (
          prev +
          JSON.parse(current[dataIndex].split('"').join('')).reduce(
            (sum: number, [_, count]: [any, number]) => sum + count,
            0,
          )
        );
      }, 0);
      const invalid = !totalLogs;
      if (invalid) setInvalidData('no data'); // override density color to dark drey

      const scrollData = data.chart.table.rows.reduce((prev: any, current: any) => {
        const data = JSON.parse(current[dataIndex].split('"').join(''));
        data.map(([position, count]: [number, number]) => {
          prev[Math.floor(position * 200)] += count;
        });
        return prev;
      }, lins);

      const histmapData = !invalid
        ? scrollData.map(cumulativeSub(totalLogs)).map((value: number) => (value / totalLogs) * 100)
        : [1, 2];
      render(histmapData, invalid);
    },
  });

  return { containerRef, error, loading, invalidData };
};

// reduce helper
const cumulativeSub = (sum: number) => (value: number) => (sum -= value);
const nearest = (target: number) => (prev: number, current: number) =>
  Math.abs(current - target) < Math.abs(prev - target) ? current : prev;
